import get from 'lodash/get'
import {
  CREATE,
  DELETE,
  DELETE_MANY,
  GET_LIST,
  GET_MANY,
  GET_MANY_REFERENCE,
  GET_ONE,
  UPDATE,
  UPDATE_MANY,
  convertLegacyDataProvider,
} from 'ra-core'
import { createApolloClient, fileStorage } from '~/helpers'
import buildQueryInit from './buildQuery'
import {
  ALL_TYPES as INNER_ALL_TYPES,
  MUTATION_TYPES as INNER_MUTATION_TYPES,
  QUERY_TYPES as INNER_QUERY_TYPES,
} from './constants'
import defaultResolveIntrospection from './introspection'

export const QUERY_TYPES = INNER_QUERY_TYPES
export const MUTATION_TYPES = INNER_MUTATION_TYPES
export const ALL_TYPES = INNER_ALL_TYPES

// Hasura's default root-field names, and — second — the camelCase ones nhost
// gives its own tables (auth.users is `users`/`insertUsers`/`updateUsers`).
// introspection.js takes the first name the schema actually has.
const cap = (name) => name.charAt(0).toUpperCase() + name.slice(1)

const defaultOptions = {
  resolveIntrospection: defaultResolveIntrospection,
  introspection: {
    operationNames: {
      [GET_LIST]: (resource) => [`${resource.name}`],
      [GET_ONE]: (resource) => [`${resource.name}`],
      [GET_MANY]: (resource) => [`${resource.name}`],
      [GET_MANY_REFERENCE]: (resource) => [`${resource.name}`],
      [CREATE]: (resource) => [`insert_${resource.name}`, `insert${cap(resource.name)}`],
      [UPDATE]: (resource) => [`update_${resource.name}`, `update${cap(resource.name)}`],
      [UPDATE_MANY]: (resource) => [`update_${resource.name}`, `update${cap(resource.name)}`],
      [DELETE]: (resource) => [`delete_${resource.name}`, `delete${cap(resource.name)}`],
      [DELETE_MANY]: (resource) => [`delete_${resource.name}`, `delete${cap(resource.name)}`],
    },
    exclude: undefined,
    include: undefined,
  },
}

const getOptions = (options, aorFetchType, resource) => {
  if (typeof options === 'function') {
    return options(resource, aorFetchType)
  }

  return options
}

export const raDataGraphql = (options) => {
  const {
    client: clientOptions,
    introspection,
    resolveIntrospection,
    buildQuery: buildQueryOptions = {},
    override = {},
    ...otherOptions
  } = { ...defaultOptions, ...options }

  if (override && process.env.NODE_ENV === 'production') {
    console.warn(
      // eslint-disable-line
      'The override option is deprecated. You should instead wrap the buildQuery function provided by the dataProvider you use.'
    )
  }

  let introspectionResults = {
    resources: [],
  }
  const client = createApolloClient(clientOptions.graphqlURL)

  const fetchIntrospection = async (reload = false) => {
    if (!reload && introspectionResults) {
      return introspectionResults
    }

    if (introspection) {
      introspectionResults = await resolveIntrospection(client, introspection)
    }
    return introspectionResults
  }

  const buildQuery = buildQueryInit(buildQueryOptions)(fetchIntrospection, otherOptions)

  const dataProvider = async (aorFetchType, resource, params) => {
    const overriddenBuildQuery = get(override, `${resource}.${aorFetchType}`)

    try {
      const buildQueryResults = await buildQuery(aorFetchType, resource, params, fileStorage)
      const { parseResponse, ...query } = overriddenBuildQuery
        ? {
            ...buildQueryResults,
            ...overriddenBuildQuery(params),
          }
        : buildQueryResults

      const operation = getQueryOperation(query.query)

      if (operation === 'query') {
        const apolloQuery = {
          ...query,
          fetchPolicy: 'network-only',
          ...getOptions(otherOptions.query, aorFetchType, resource),
        }

        return client.query(apolloQuery).then((response) => parseResponse(response))
      }

      const apolloQuery = {
        mutation: query.query,
        variables: query.variables,
        ...getOptions(otherOptions.mutation, aorFetchType, resource),
      }

      return client.mutate(apolloQuery).then(parseResponse)
    } catch (e) {
      return Promise.reject(e)
    }
  }

  // react-admin 3 accepted this legacy (type, resource, params) signature and
  // adapted it internally; v4 dropped that, and <Admin> now calls
  // dataProvider.getList(...) directly. ra-core still ships the adapter, so use
  // it rather than hand-rolling the nine-method mapping.
  //
  // `saga` and `observeRequest` are gone with it: react-admin 5 has no sagas,
  // and observeRequest called the (now async) buildQuery synchronously, so it
  // could not have worked. Neither had a caller.
  return { client, dataProvider: convertLegacyDataProvider(dataProvider) }
}

const getQueryOperation = (query) => {
  if (query && query.definitions && query.definitions.length > 0) {
    return query.definitions[0].operation
  }

  throw new Error('Unable to determine the query operation')
}
